import React, { useMemo, useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useTheme } from '@mui/material/styles';
import { reportingApi } from 'src/api/reporting';
import { CircularProgress, Box } from '@mui/material';
import hexToRgbA from 'src/utils/hexToRgbA';

// Add organization interface
interface Organization {
  id: string;
  name: string;
  reportingLowerLimit?: number;
  reportingUpperLimit?: number;
}

interface MixedChartProps {
  selectedOrganization: string;
  organizationData?: Organization;
}

const MixedChart: React.FC<MixedChartProps> = ({
  selectedOrganization,
  organizationData,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{
    bars: { name: string; data: number[]; group: string; color: string }[];
    categories: string[];
  }>({
    bars: [],
    categories: [],
  });

  const utilizedColors = [
    '#003f5c',
    '#374c80',
    '#7a5195',
    '#bc5090',
    '#ef5675',
    '#ff764a',
    '#ffa600',
  ];

  const allocatedColors = [
    '#5791AC',
    '#91A3D3',
    '#BCA8CA',
    '#DDA7C8',
    '#FF9CB2',
    '#FFC1AD',
    '#FFE484',
  ];

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: {
        show: false,
      },
      background: 'transparent',
      animations: {
        enabled: true,
        speed: 500,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    stroke: {
      width: 1,
      colors: ['transparent'],
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '80%',
        borderRadiusApplication: 'around',
        borderRadiusWhenStacked: 'all',
        borderRadius: 2,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: false,
      formatter(val: any, opts?: any) {
        if (val < 70) return '';
        return val ? val.toFixed(1) : '';
      },
      style: {
        fontSize: '10px',
        fontFamily: 'Quicksand',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
    },
    xaxis: {
      categories: data.categories,
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: 'Quicksand',
          colors: theme.palette.text.secondary,
        },
        rotateAlways: false,
        hideOverlappingLabels: true,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      title: {
        text: 'Hours',
        style: {
          fontSize: '13px',
          fontFamily: 'Quicksand',
          color: theme.palette.text.secondary,
        },
      },
      labels: {
        formatter: (value) => value?.toFixed(2),
        style: {
          colors: theme.palette.text.secondary,
        },
      },
      min: organizationData?.reportingLowerLimit || 0,
      max: organizationData?.reportingUpperLimit || undefined,
      tickAmount: 5,
      forceNiceScale: true,
    },
    tooltip: {
      shared: false,
      intersect: true,
      y: {
        formatter: (value) => (value ? `${value.toFixed(2)} hours` : undefined),
      },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      offsetY: 8,
      itemMargin: {
        horizontal: 12,
        vertical: 5,
      },
      fontSize: '13px',
    },
    grid: {
      borderColor: theme.palette.divider,
    },
    colors: undefined,
  };

  useEffect(() => {
    const fetchMixedChartData = async () => {
      if (!selectedOrganization) return;

      try {
        setLoading(true);
        const response = await reportingApi.getMixChartData({
          organizationId: selectedOrganization,
        });

        response.data = response.data.reverse();
        const categories = response.data.map((sprint) => sprint.sprintName);

        // Create series for each unique account
        const accountSeries: {
          [key: string]: { allocated: number[]; utilized: number[] };
        } = {};

        // First pass: collect all unique accounts and initialize their data arrays
        response.data.forEach((sprint) => {
          sprint.details.forEach((detail) => {
            if (!accountSeries[detail.accountName]) {
              accountSeries[detail.accountName] = {
                allocated: new Array(response.data.length).fill(0),
                utilized: new Array(response.data.length).fill(0),
              };
            }
          });
        });

        // Second pass: fill in the data
        response.data.forEach((sprint, sprintIndex) => {
          sprint.details.forEach((detail) => {
            accountSeries[detail.accountName].allocated[sprintIndex] =
              detail.totalHoursAllocated;
            accountSeries[detail.accountName].utilized[sprintIndex] =
              detail.totalHoursUtilized;
          });
        });

        // Modified transformation
        const bars = Object.entries(accountSeries).reduce(
          (acc, [accountName, accountData], index) => {
            const colorIndex = index % 7; // Cycle through 7 colors
            return [
              ...acc,
              {
                name: `${accountName} (Allocated)`,
                group: 'Allocated',
                data: accountData.allocated,
                color: hexToRgbA(allocatedColors[colorIndex], 1), // Use allocated colors
              },
              {
                name: `${accountName} (Utilized)`,
                group: 'Utilized',
                data: accountData.utilized,
                color: hexToRgbA(utilizedColors[colorIndex], 1), // Use utilized colors
              },
            ];
          },
          []
        );

        setData({
          bars,
          categories,
        });
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMixedChartData();
  }, [selectedOrganization]);

  const maxValue = useMemo(() => {
    const allValues = data.bars.flatMap((bar) => bar.data);
    const dataMax = Math.max(...allValues);

    if (selectedOrganization === '64e4d2c228dea22ee9d5245f') {
      return Math.max(400, dataMax);
    }

    return dataMax;
  }, [data, selectedOrganization]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          height: '75%',
          alignItems: 'center',
        }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <ReactApexChart
      options={options}
      series={data.bars}
      type="bar"
      height={350}
    />
  );
};

export default MixedChart;
